import { useEffect, useRef, useState } from 'react';
import type { Photo } from '../../lib/types';
import { Icon } from '../ui/Icon';

export function PhotoGallery({ photos, onPrimary, onRemove }: {
  photos: Photo[];
  onPrimary: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const returnTarget = useRef<HTMLButtonElement | null>(null);
  const close = () => { setActiveIndex(null); requestAnimationFrame(() => returnTarget.current?.focus()); };
  useEffect(() => {
    if (activeIndex === null) return;
    closeButton.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowRight') setActiveIndex((value) => value === null ? null : (value + 1) % photos.length);
      if (event.key === 'ArrowLeft') setActiveIndex((value) => value === null ? null : (value - 1 + photos.length) % photos.length);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [activeIndex, photos.length]);

  if (photos.length === 0) return <p className="muted">No photographs have been added.</p>;
  const primary = photos.find((photo) => photo.is_primary) ?? photos[0]!;
  return <>
    <div className="photo-gallery">
      <button className="photo-gallery__primary" onClick={(event) => { returnTarget.current = event.currentTarget; setActiveIndex(photos.indexOf(primary)); }}><img src={primary.url} alt="Open primary photograph"/></button>
      <div className="photo-gallery__thumbs">{photos.map((photo, index) => <figure key={photo.id}>
        <button className={photo.is_primary ? 'is-primary' : ''} onClick={(event) => { returnTarget.current = event.currentTarget; setActiveIndex(index); }}><img src={photo.thumbnail_url ?? photo.url} alt={`Open photograph ${index + 1}`}/></button>
        <figcaption>{photo.is_primary ? <span>Primary</span> : <button onClick={() => onPrimary(photo.id)}>Make primary</button>}<button className="danger-link" onClick={() => onRemove(photo.id)}>Remove</button></figcaption>
      </figure>)}</div>
    </div>
    {activeIndex !== null ? <div className="lightbox" role="dialog" aria-modal="true" aria-label="Photograph viewer">
      <button ref={closeButton} className="lightbox__close" onClick={close} aria-label="Close photograph"><Icon name="close"/></button>
      {photos.length > 1 ? <button className="lightbox__previous" onClick={() => setActiveIndex((activeIndex - 1 + photos.length) % photos.length)} aria-label="Previous photograph"><Icon name="back"/></button> : null}
      <img src={photos[activeIndex]!.url} alt={`Photograph ${activeIndex + 1} of ${photos.length}`}/>
      {photos.length > 1 ? <button className="lightbox__next" onClick={() => setActiveIndex((activeIndex + 1) % photos.length)} aria-label="Next photograph"><Icon name="chevron"/></button> : null}
      <span>{activeIndex + 1} / {photos.length}</span>
    </div> : null}
  </>;
}
